import React, { useState, useEffect, useRef } from 'react';
import SelectField from '../../components/SelectField';
import InfoCard from '../../components/InfoCard';
import LoadingState, { InlineSpinner } from '../../components/LoadingState';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import {
  FaArrowLeft,
  FaChartBar,
  FaChartLine,
  FaCheckCircle,
  FaComments,
  FaCopy,
  FaExclamationTriangle,
  FaFileAlt,
  FaPen,
  FaPhone,
  FaSave,
  FaStopwatch,
  FaTimes,
  FaUserShield
} from 'react-icons/fa';
import { parseCallFilename } from '../../common/auditFormat';
import { getCallDetails, genaiQuery, genairefresh, swap, override, markAssessed, unmarkAssessed, markNonSales, unmarkNonSales, updateCallMetadata, dispute, resolveDispute } from '../../api/api';
import {
  ASSESSMENT_TYPE,
  ASSESSMENT_TYPE_LABELS,
  gateSteps,
  hasConductQuestions,
  isComplianceOnlyScorecard,
  isNonSalesCall,
  isNotApplicable,
  nonSalesDetailsOf,
  isNotScored,
  isUnchecked,
  answerOf,
  successOf,
} from '../../common/assessment';
import PreCheckSteps, { PreCheckStepsEditor, stepAnswerConflict } from './PreCheckSteps';
import EditMetadataModal from '../../components/EditMetadataModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import DisputeModal from '../../components/DisputeModal';
import NonSalesModal from '../../components/NonSalesModal';
import ResolveDisputeModal from '../../components/ResolveDisputeModal';
import { Formatter } from '../../format';
import { SentimentIcon, SentimentBar, SentimentText } from '../../components/SentimentIcon';
import { TrendIcon } from '../../components/TrendIcon';
import ChatInput from '../../components/ChatInput';
import AudioPlayer from '../../components/AudioPlayer';
import CallDetailsPanel from '../../components/CallDetailsPanel';
import { SentimentChart } from '../../components/SentimentChart';
import { SpeakerTimeChart } from '../../components/SpeakerTimeChart';
import { TranscriptSegment } from '../../components/TranscriptSegment';
import { TranscriptOverlay } from '../../components/TranscriptOverlay';
import { getEntityColor } from '../../components/colours';
import './dashboard.css';
import RefreshButton from '../../components/RefreshButton';
import SwapSpeakersButton from '../../components/SwapSpeakersButton';
import { canSwapSpeakers } from '../../common/speakerSwap';
import { LoudnessChart } from "./LoudnessChart";
import { ComprehendSentimentChart } from "./ComprehendSentimentChart";
import useIsSuperAdmin from '../../hooks/useIsSuperAdmin';
import useIsAdmin from '../../hooks/useIsAdmin';
import useIsQAAgent from '../../hooks/useIsQAAgent';
import useIsCallAgent from '../../hooks/useIsCallAgent';
import AssessmentCriterion, { StatusPill } from '../../components/AssessmentCriterion';

/* Verdict rules, kept in one place so the criterion cards and the section counts
   above them can never disagree about what passed. */
const isComplianceItemPassed = (parsed) => {
  if (parsed.passed !== undefined) return parsed.passed;
  const answer = answerOf(parsed);
  const success = successOf(parsed);
  return answer === success;
};

const isQualityItemFailed = (parsed) => {
  const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;
  const answer = answerOf(parsed);
  const success = successOf(parsed);
  return (answer !== success && isCriticalFail) || parsed.status === 'failed';
};

/* Section header carrying an at-a-glance tally, so a reviewer knows how many
   criteria need attention without reading every card. */
const SectionHeading = ({ title, items }) => {
  const failed = items.filter((ok) => !ok).length;

  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h5>
      <span className="text-xs text-gray-500">
        {failed > 0 ? (
          <span className="font-semibold text-red-700">
            {failed} of {items.length} need attention
          </span>
        ) : (
          <span className="font-semibold text-green-700">All {items.length} passed</span>
        )}
      </span>
    </div>
  );
};

const Dashboard = ({ setAlert, userEmail, username }) => {
  // v7 wildcard: route is `/dashboard/*`, value lives in params['*']
  const params = useParams();
  const key = params['*'] || params.key;
  const navigate = useNavigate();
  const { data: callData, error } = useSWR(
    key ? `/get/${key}` : null,
    () => getCallDetails(key),
    { revalidateOnFocus: false }
  );
  const loading = !callData && !error;
  const isTranscribeCallAnalyticsMode =
    callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo
      ?.TranscribeApiType === "analytics";
  const isSuperAdmin = useIsSuperAdmin();
  const isAdmin = useIsAdmin();
  const isQAAgent = useIsQAAgent();
  // Same people who may Refresh Call; the swap re-runs the evaluation too.
  const maySwapSpeakers = (isSuperAdmin || isAdmin || isQAAgent) &&
    canSwapSpeakers(callData?.ConversationAnalytics);
  const [loudnessData, setLoudnessData] = useState({});
  const [, setActiveTab] = useState('overview');
  const [showEditMetadata, setShowEditMetadata] = useState(false);
  const [genAiHistory, setGenAiHistory] = useState([]);
  const audioRef = useRef(null);
  const audioTimeRef = useRef(0);
  const transcriptRef = useRef(null);
  const [speakerLabels, setSpeakerLabels] = useState({});
  // The section jump-nav is only useful once Overview has scrolled away —
  // shown from the top it just covers content you can already see.
  const [showSectionNav, setShowSectionNav] = useState(false);
  const { mutate } = useSWRConfig();

  const range = (start, end) => {
    return [...Array(end + 1 - start).keys()].map((x) => x + start);
  };

  const toFiniteNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const sentimentLabelToScore = (label) => {
    if (!label || typeof label !== 'string') return null;
    const normalized = label.trim().toUpperCase();
    if (normalized === 'POSITIVE') return 1;
    if (normalized === 'NEGATIVE') return -1;
    if (normalized === 'NEUTRAL') return 0;
    if (normalized === 'MIXED') return 0;
    return null;
  };

  const extractScoreFromObject = (score) => {
    if (!score || typeof score !== 'object') return null;

    const positive = toFiniteNumber(score.Positive ?? score.positive);
    const negative = toFiniteNumber(score.Negative ?? score.negative);
    if (positive !== null || negative !== null) {
      return (positive ?? 0) - (negative ?? 0);
    }

    const overall =
      toFiniteNumber(score.Score ?? score.score ?? score.Overall ?? score.overall ?? score.Value ?? score.value);
    if (overall !== null) {
      return overall;
    }

    return null;
  };

  const normalizeFlag = (value) => {
    if (value === true || value === 'true') return 1;
    if (value === false || value === 'false' || value === null || value === undefined) return 0;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? 1 : 0;
  };

  const deriveSegmentSentimentScore = (segment) => {
    if (!segment) return 0;

    const scoreObjectValue = extractScoreFromObject(segment.SentimentScore);
    if (scoreObjectValue !== null) {
      const labelScore = sentimentLabelToScore(segment.Sentiment);
      if (labelScore !== null) {
        if (scoreObjectValue === 0) {
          return labelScore;
        }
        const haveSameDirection = Math.sign(scoreObjectValue) === Math.sign(labelScore);
        return haveSameDirection || Math.sign(labelScore) === 0
          ? scoreObjectValue
          : Math.abs(scoreObjectValue) * Math.sign(labelScore);
      }
      return scoreObjectValue;
    }

    const numericScore = toFiniteNumber(segment.SentimentScore);
    if (numericScore !== null) {
      const positiveFlag = normalizeFlag(segment.SentimentIsPositive);
      const negativeFlag = normalizeFlag(segment.SentimentIsNegative);
      const flagDelta = positiveFlag - negativeFlag;
      if (flagDelta !== 0) {
        return numericScore * Math.sign(flagDelta);
      }
      const labelScore = sentimentLabelToScore(segment.Sentiment);
      if (labelScore !== null) {
        return numericScore * (labelScore === 0 ? 0 : Math.sign(labelScore));
      }
      return numericScore;
    }

    const averageScore = toFiniteNumber(segment.AverageSentimentScore);
    if (averageScore !== null) {
      const labelScore = sentimentLabelToScore(segment.Sentiment);
      if (labelScore !== null && labelScore !== 0) {
        return averageScore * Math.sign(labelScore);
      }
      return averageScore;
    }

    const labelScore = sentimentLabelToScore(segment.Sentiment);
    if (labelScore !== null) {
      return labelScore;
    }

    const fallbackDelta =
      normalizeFlag(segment.SentimentIsPositive) - normalizeFlag(segment.SentimentIsNegative);
    if (fallbackDelta !== 0) {
      return Math.sign(fallbackDelta);
    }

    return 0;
  };

  const createLoudnessData = (segment) => {
    const derivedScore = deriveSegmentSentimentScore(segment);
    const sentimentBucket = derivedScore > 0 ? 5 : derivedScore < 0 ? -5 : 0;
    const start = Math.floor(segment.SegmentStartTime);
    const end = Math.floor(segment.SegmentEndTime);
    const r = range(start, end);
    return r.map((item, i) => ({
      x: item,
      y: segment.LoudnessScores[i],
      interruption: segment.SegmentInterruption && item === start ? 100 : null,
      sentiment: sentimentBucket,
      sentimentScore: derivedScore,
      silence: (segment.LoudnessScores[i] === 0 ? true : false)
    }));
  };

  const createSentimentData = (segment) => {
    const derivedScore = deriveSegmentSentimentScore(segment);
    const start = Math.floor(segment.SegmentStartTime);
    const end = Math.floor(segment.SegmentEndTime);
    const r = range(start, end);
    return r.map((item) => ({
      x: item,
      y: derivedScore
    }));
  }

  useEffect(() => {
    if (error) {
      console.error('Error fetching call details:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to load call details. Please try again.'
      });
    }
  }, [error, setAlert]);

  useEffect(() => {
    const labels = callData?.ConversationAnalytics?.SpeakerLabels || [];
    const newSpeakerLabels = {
      NonTalkTime: "Silence",
      Interruptions: "Interruptions",
      Positive: "Positive",
      Negative: "Negative",
      Neutral: "Neutral"
    };
    labels.forEach(({ Speaker, DisplayText }) => {
      newSpeakerLabels[Speaker] = DisplayText;
    });
    setSpeakerLabels(newSpeakerLabels);

    /*setGenAiQueries(
      (data?.ConversationAnalytics?.GenAiQuery ?
        Object.entries(data?.ConversationAnalytics?.GenAiQuery).map(([key, value]) => {
          return {
            label: key,
            value: (value instanceof Array ? value.join(', ') : value)
          }
        }) : [])
    );*/
  }, [callData]);

  useEffect(() => {
    const loudness = {};

    if (isTranscribeCallAnalyticsMode) {
      // TCA mode
      let interruptions = [];
      let silence = [];
      let positive = [];
      let negative = [];
      let neutral = [];
      let nonSilence = [];

      Object.keys(speakerLabels).forEach(key => {
        let keyLoudness = (callData?.SpeechSegments || [])
        .filter((segment) => segment.SegmentSpeaker === key)
        .map(createLoudnessData)
        .flat();
        
        loudness[key] = keyLoudness;
        let newInterruptions = keyLoudness.filter((d) => d.interruption)
          .map((d) => ({ y: d.interruption, x: d.x }))
        interruptions = interruptions.concat(newInterruptions)

        let newSilence = keyLoudness.filter((d) => d.silence)
          .map((d) => ({ x: d.x, y: 100 }))
        silence = silence.concat(newSilence);

        keyLoudness.forEach((item) => {
          let sentimentItem = {
            x: item.x,
            y: 10,
            sentiment: item.sentiment
          };
          if (item.sentiment > 0) positive.push(sentimentItem)
          else if (item.sentiment < 0) negative.push(sentimentItem)
          else neutral.push(sentimentItem);
          nonSilence[item.x.toString()] = sentimentItem;
        });

      });
      
      // generate the rest of the silence
      if (callData) {
        const r = range(0, parseInt(callData?.ConversationAnalytics.Duration));
        r.forEach((item, i) => {
          if (!(i in nonSilence)) {
            silence = silence.concat({ x: i, y: 100 });
          }
        });
      }

      loudness['Interruptions'] = interruptions;
      loudness['NonTalkTime'] = silence;
      loudness['Positive'] = positive;
      loudness['Neutral'] = neutral;
      loudness['Negative'] = negative;
    } else {
      // this is transcribe standard
      Object.keys(speakerLabels).forEach(key => {
        if (key.indexOf('spk_') >= 0) {
          let keyLoudness = (callData?.SpeechSegments || [])
          .filter((segment) => segment.SegmentSpeaker === key)
          .map(createSentimentData)
          .flat();
          loudness[key] = keyLoudness;
        }
      });
    }
    setLoudnessData(loudness);
  }, [speakerLabels])

  // useEffect(() => {
  //   const labels = callData?.ConversationAnalytics?.SpeakerLabels || [];
  //   const mapping = {};
  //   labels.forEach(({ Speaker, DisplayText }) => {
  //     mapping[Speaker] = DisplayText;
  //   });
  //   setSpeakerLabels(mapping);
  // }, [callData]);

  useEffect(() => {
    return () => {
      if (audioTimeRef.current > 0) {
        sessionStorage.setItem(`audioTime_${key}`, audioTimeRef.current);
      }
    };
  }, [key]);

  const handleGenAiSubmit = async (text) => {
    if (!callData) return;
    const entry = { question: text, answer: '...' };
    setGenAiHistory((h) => [...h, entry]);
    try {
      const result = await genaiQuery(key || callData.key, text);
      setGenAiHistory((h) =>
        h.map((e) => (e === entry ? { ...e, answer: result.response } : e))
      );
    } catch (error) {
      setGenAiHistory((h) =>
        h.map((e) => (e === entry ? { ...e, answer: 'Error fetching answer' } : e))
      );
    }
  };

  const audioEndTimestamps = (callData?.SpeechSegments || [])
    .map((s) => s.WordConfidence || [])
    .flat()
    .reduce((acc, item) => [...acc, item.EndTime], []);

  const onAudioPlayTimeUpdate = () => {
    if (!audioRef.current || !transcriptRef.current) return;
    audioTimeRef.current = audioRef.current.currentTime;
    let elementEndTime;
    for (let i = 0; i < audioEndTimestamps.length; i++) {
      if (audioRef.current.currentTime < audioEndTimestamps[i]) {
        elementEndTime = audioEndTimestamps[i];
        break;
      }
    }

    [...transcriptRef.current.getElementsByClassName('playing')].forEach(elem =>
      elem.classList.remove('playing')
    );
    const el = transcriptRef.current.querySelector(
      `span[data-end="${elementEndTime}"]`
    );
    el && el.classList.add('playing');
  };

  // Reveal the jump-nav only once the Overview panel has scrolled out of view.
  useEffect(() => {
    const overview = document.getElementById('overview');
    if (!overview || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowSectionNav(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(overview);
    return () => observer.disconnect();
  }, [callData]);

  if (loading) {
    return (
      <LoadingState variant="detail" label="Loading call details" />
    );
  }

  if (!callData) {
    return (
      <div className="py-10 px-6 text-center">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
          <FaFileAlt className="text-lg" />
        </div>
        <h3 className="mt-3 text-sm font-semibold text-gray-800">Call not found</h3>
        <p className="mt-1 text-sm text-gray-400">
          The requested call could not be found.
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            <FaArrowLeft /> Back to Call List
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FaChartBar /> },
    { id: 'transcript', label: 'Transcript', icon: <FaFileAlt /> },
    { id: 'analytics', label: 'Analytics', icon: <FaChartLine /> },
    { id: 'qa', label: 'QA Assessment', icon: <FaCheckCircle /> },
    // { id: 'genai', label: 'GenAI', icon: <FaComments /> }
  ];

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const genAiSummary = (() => {
    let summary = callData?.ConversationAnalytics?.Summary
      ? Object.entries(callData.ConversationAnalytics.Summary)
          .filter(([key]) => key !== 'Validations' && key !== 'Scores')  // Exclude validation data and scores - they're displayed in separate sections
          .map(([key, value]) => ({
            label: key,
            value: Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : value,
          }))
      : [];

    const contactSummary =
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
    if (contactSummary) {
      summary = [{ label: 'Contact Summary', value: contactSummary }, ...summary];
    }
    return summary;
  })();

  const refreshSummary = async (template) => {
    if (!callData) return;
    
    // Extract templateName from the template object, or use template directly if it's a string
    const templateName = template?.templateName || template || '';

    await genairefresh(key || callData.filename, templateName);
    await mutate(`/get/${key}`);
  };

  const swapSpeakers = async () => {
    const result = await swap(key || callData.filename);
    await mutate(`/get/${key}`);
    return result;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        {(() => {
          // The stored filename packs agent/campaign/job metadata into one
          // huge string — parse the identity out of it for the header and
          // demote the raw name to a copyable one-liner.
          const rawName =
            callData.jobName ||
            callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo?.TranscriptionJobName ||
            '';
          const parsed = parseCallFilename(rawName);
          const campaign = callData?.ConversationAnalytics?.Cust;
          const template = callData?.ConversationAnalytics?.Template;
          const initials = parsed.agent
            ? parsed.agent.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('')
            : null;
          return (
            <div className="flex items-start gap-4 min-w-0">
              <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white flex items-center justify-center flex-shrink-0 shadow-sm uppercase text-base font-bold">
                {initials || <FaPhone />}
              </span>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 leading-snug truncate">
                  {parsed.agent || 'Call Details'}
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Call Details • {Formatter.Timestamp(
                    callData.timestamp || callData?.ConversationAnalytics?.ConversationTime
                  )}
                </p>
                {(campaign || template) && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {campaign && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                        {campaign}
                      </span>
                    )}
                    {template && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium">
                        {template}
                      </span>
                    )}
                  </div>
                )}
                {rawName && (
                  <p className="flex items-center gap-1 mt-2 min-w-0">
                    <span className="font-mono text-[11px] text-gray-400 truncate" title={rawName}>
                      {rawName}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(rawName);
                        setAlert?.({ variant: 'success', heading: 'Copied', text: 'Filename copied to clipboard.' });
                      }}
                      className="p-1 rounded-md text-gray-400 hover:text-cloudza-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                      aria-label="Copy filename"
                      title="Copy filename"
                    >
                      <FaCopy className="w-3 h-3" />
                    </button>
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        <div className="flex items-center space-x-3 flex-shrink-0">
          <RefreshButton
            onRefresh={(template) => refreshSummary(template)}
            setAlert={setAlert}
            showTemplateDropdown={true}
            campaign={callData?.ConversationAnalytics?.Cust}
            currentTemplate={callData?.ConversationAnalytics?.Template}
            label="Refresh Call"
          />
        </div>
      </div>

      <EditMetadataModal
        open={showEditMetadata}
        callData={callData?.ConversationAnalytics}
        onSave={async (metadata) => {
          await updateCallMetadata(key, metadata, userEmail);
          await mutate(`/get/${key}`);
          setShowEditMetadata(false);
          setAlert({ variant: 'success', heading: 'Metadata Updated', text: 'Call metadata has been saved successfully.' });
        }}
        onClose={() => setShowEditMetadata(false)}
      />

      <CallDetailsPanel callData={callData} onEditMetadata={isSuperAdmin ? () => setShowEditMetadata(true) : undefined} />

      {isTranscribeCallAnalyticsMode && (
        <div className="card">
          <h2 className="text-sm font-bold text-gray-900 mb-4">
            Loudness/Sentiment
          </h2>
          {(!loudnessData || Object.keys(loudnessData).length === 0) && !error ? (
            <div key='noSpeakers' className="text-sm text-gray-400">No Speakers</div>
          ) : (
            <LoudnessChart loudnessData={loudnessData} speakerLabels={speakerLabels} />
          )}
        </div>
      )}
      {!isTranscribeCallAnalyticsMode && (
        <div className="card">
          <h2 className="text-sm font-bold text-gray-900 mb-4">
            Comprehend Sentiment
          </h2>
          {(!loudnessData || Object.keys(loudnessData).length === 0) && !error ? (
            <div key='noSpeakers' className="text-sm text-gray-400">No Speakers</div>
          ) : (
            <ComprehendSentimentChart comprehendSentimentData={loudnessData} speakerLabels={speakerLabels} />
          )}
        </div>
      )}

      {/* Key Metrics — auto-fit rather than a fixed column count, because the
          number of cards varies with what the call has (quality score,
          compliance, conduct). A fixed grid strands the last one alone on a
          second row. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
        {/* Quality Score (v2.0) or QA Score (legacy). A compliance-only scorecard has no QA
            questions, so its QA and Compliance scores are always 0 - not scores at all - and the
            call is judged on the Compliance and Critical Fail tiles instead. */}
        {!isComplianceOnlyScorecard(callData) && (
        <MetricCard
          title={callData.ConversationAnalytics?.Summary?.Scores?.qualityScore !== undefined ? "Quality Score" : "QA Score"}
          value={callData.ConversationAnalytics?.Summary?.Scores?.qualityScore !== undefined
            ? `${callData.ConversationAnalytics.Summary.Scores.qualityScore.toFixed(1)}%`
            : `${(callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score ?? 0)}/100`}
          status={
            (callData.ConversationAnalytics?.Summary?.Scores?.qualityScore ??
             callData.ConversationAnalytics?.Rating?.QA_Score ??
             callData.Result?.QA_Score ?? 0) >= 80 ? 'success' :
            (callData.ConversationAnalytics?.Summary?.Scores?.qualityScore ??
             callData.ConversationAnalytics?.Rating?.QA_Score ??
             callData.Result?.QA_Score ?? 0) >= 60 ? 'warning' : 'error'
          }
          icon={<FaChartBar />}
        />
        )}

        {/* Compliance Score Percentage */}
        {!isComplianceOnlyScorecard(callData) &&
         (callData.ConversationAnalytics?.Summary?.Scores?.complianceScore !== undefined ||
          callData.ConversationAnalytics?.Rating?.ComplianceScore !== undefined) && (
          <MetricCard
            title="Compliance Score"
            value={`${(callData.ConversationAnalytics?.Summary?.Scores?.complianceScore ??
                       callData.ConversationAnalytics?.Rating?.ComplianceScore ?? 0).toFixed(1)}%`}
            status={
              (callData.ConversationAnalytics?.Summary?.Scores?.complianceScore ??
               callData.ConversationAnalytics?.Rating?.ComplianceScore ?? 0) >= 80 ? 'success' :
              (callData.ConversationAnalytics?.Summary?.Scores?.complianceScore ??
               callData.ConversationAnalytics?.Rating?.ComplianceScore ?? 0) >= 60 ? 'warning' : 'error'
            }
            icon={<FaCheckCircle />}
          />
        )}

        {/* Compliance Pass/Fail Status */}
        <MetricCard
          title="Compliance"
          value={(callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'Pass' : 'Fail'}
          status={(callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'success' : 'error'}
          icon={<FaCheckCircle />}
        />

        {/* Agent Conduct Pass/Fail - only when the scorecard actually asked a conduct question.
            Rating.Conduct defaults to true, so testing it directly would claim a conduct verdict
            on calls where conduct was never assessed. */}
        {hasConductQuestions(callData) && (
          <MetricCard
            title="Agent Conduct"
            value={callData.ConversationAnalytics?.Rating?.Conduct === false ? 'Fail' : 'Pass'}
            status={callData.ConversationAnalytics?.Rating?.Conduct === false ? 'error' : 'success'}
            icon={<FaUserShield />}
          />
        )}

        {/* Critical Fail Indicator */}
        {callData.ConversationAnalytics?.Rating?.CriticalFail && (
          <MetricCard
            title="Critical Fail"
            value="FAILED"
            status="error"
            icon={<FaCheckCircle />}
            subtitle={callData.ConversationAnalytics?.Summary?.Scores?.criticalFailReason || 'Critical requirement not met'}
          />
        )}
        
        <MetricCard
          title="Duration"
          value={Formatter.Time(Math.round(callData?.ConversationAnalytics?.Duration ?? callData.duration))}
          status="neutral"
          icon={<FaStopwatch />}
        />
        
      </div>

      {/* Tab Navigation */}
      {/* This wrapper holds the sections too, so it keeps its normal spacing —
          only the nav pill inside it collapses when hidden. */}
      <div className="relative">
        {/* Floating Tab Navigation */}
        <div
          className={`sticky top-4 z-30 flex justify-center transition-opacity duration-200 ${
            showSectionNav
              ? 'opacity-100 mb-8'
              : 'opacity-0 pointer-events-none h-0 mb-0 overflow-hidden'
          }`}
          aria-hidden={!showSectionNav}
          data-tour="section-nav"
        >
          {/* Compact on phones (tight padding, xs labels, single-line flex
              buttons so icons can't wrap above their labels), roomy from sm up. */}
          <div className="section-nav-pill max-w-full px-1.5 py-1.5 sm:px-2 sm:py-2 rounded-full shadow-lg border flex gap-1 sm:gap-2">
            <button
              onClick={(e) => scrollToSection(e, 'overview')}
              className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all active:scale-95"
            >
              <FaChartBar className="flex-shrink-0" />
              Overview
            </button>
            <button
              onClick={(e) => scrollToSection(e, 'transcript')}
              className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all active:scale-95"
            >
              <FaComments className="flex-shrink-0" />
              Transcript
            </button>
            <button
              onClick={(e) => scrollToSection(e, 'qa')}
              className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all active:scale-95"
            >
              <FaCheckCircle className="flex-shrink-0" />
              QA
            </button>
          </div>
        </div>

        {/* Sections with IDs added */}
        <CollapsibleSection id="overview" title="Overview">
          <OverviewTab callData={callData} genAiSummary={genAiSummary} />
        </CollapsibleSection>

        <CollapsibleSection id="transcript" title="Transcript">
          <TranscriptTab
            callData={callData}
            speakerLabels={speakerLabels}
            audioRef={audioRef}
            transcriptRef={transcriptRef}
            deriveSentimentScore={deriveSegmentSentimentScore}
            onAudioPlayTimeUpdate={onAudioPlayTimeUpdate}
            callKey={key}
            swapAction={maySwapSpeakers && (
              <SwapSpeakersButton
                onSwap={swapSpeakers}
                setAlert={setAlert}
                template={callData?.ConversationAnalytics?.Template}
                className="!py-1 !px-3 !text-xs flex-shrink-0"
              />
            )}
          />
        </CollapsibleSection>

        <CollapsibleSection id="qa" title="QA">
            <QATab
              callKey={key}
              userEmail={userEmail}
              username={username}
              callData={callData}
              onRefresh={(template) => refreshSummary(template)}
              setAlert={setAlert}
              campaign={callData?.ConversationAnalytics?.Cust}
              currentTemplate={callData?.ConversationAnalytics?.Template}
              summary={genAiSummary}
              transcript={callData.transcript}
              onRefr={() => refreshSummary()}
              history={genAiHistory}
              onSubmit={handleGenAiSubmit}
            />
        </CollapsibleSection>
      </div>
    </div>
  );
};

// Metric Card Component
// Collapsible panel for the call-detail sections. Long transcripts otherwise
// push the QA panel far down the page, so each section can be folded away.
const CollapsibleSection = ({ id, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div id={id} className="card !p-0 mb-6 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        className={`w-full flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors ${open ? 'border-b border-gray-100' : ''}`}
      >
        <h2 className="text-sm font-bold text-gray-900">{title}</h2>
        <span className="flex items-center gap-2 text-xs font-medium text-gray-400">
          {open ? 'Hide' : 'Show'}
          <svg
            className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {/* Kept mounted so audio playback and scroll refs survive collapsing. */}
      <div id={`${id}-panel`} className={`p-6 ${open ? '' : 'hidden'}`}>
        {children}
      </div>
    </div>
  );
};

// Matches the KPI cards on Campaign Home and Jobs: neutral icon tile, ink
// value, and a status dot carrying pass/fail. Colouring the number as well as
// the tile made a row of these read as a rainbow rather than one set.
const MetricCard = ({ title, value, status, icon, subtitle }) => {
  const statusDot = {
    success: 'bg-coral-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    neutral: 'bg-gray-300'
  };

  return (
    <div className="card-hover">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1 truncate flex items-center gap-2">
            {status && status !== 'neutral' && (
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot[status]}`} aria-hidden="true" />
            )}
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1 truncate">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 w-9 h-9 bg-gray-50 text-gray-400 rounded-xl flex items-center justify-center text-sm">
          {icon}
        </div>
      </div>
    </div>
  );
};

/* A fact with no value used to render as a label above empty space, which reads
   as a broken panel rather than as "not detected". */
const Fact = ({ label, value, children }) => {
  const isEmpty = children === undefined && (value === undefined || value === null || value === '');

  return (
    <div className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-gray-900 truncate" title={isEmpty ? undefined : value}>
        {children ?? (isEmpty ? <span className="text-gray-400">Not detected</span> : value)}
      </dd>
    </div>
  );
};

const ResolvedPill = ({ value }) => {
  if (!value) return <span className="text-gray-400">Not detected</span>;
  const yes = value === 'Yes';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${
        yes ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
      }`}
    >
      {yes ? <FaCheckCircle /> : <FaTimes />}
      {value}
    </span>
  );
};

// Agent core writes its verdict into Summary as OverallAssessment /
// overall_assessment — {summary, confidence} as an object or a JSON string,
// which the generic label/value list can only show as raw JSON.
const SUMMARY_FAILED_PLACEHOLDER = 'Click reload to generate';
const getOverallAssessment = (callData) => {
  const s = callData?.ConversationAnalytics?.Summary;
  if (!s) return null;
  const key = Object.keys(s).find(
    (k) => k.toString().trim().toLowerCase().replace(/[_\s]/g, '') === 'overallassessment'
  );
  if (!key) return null;
  let v = s[key];
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return { summary: v };
    }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
};

// Overview Tab
const OverviewTab = ({ callData, genAiSummary = [] }) => {
  const resolved =
    callData.summary_resolved || callData?.ConversationAnalytics?.ContactSummary?.Resolved;
  const overallAssessment = getOverallAssessment(callData);
  const storedSummary =
    callData.summary_summary ||
    callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
  // The summarize step stores the literal placeholder when it failed; the
  // agent-core assessment carries the real prose in that case.
  const summaryProse =
    storedSummary && storedSummary !== SUMMARY_FAILED_PLACEHOLDER
      ? storedSummary
      : overallAssessment?.summary || storedSummary;
  const proseFromAssessment =
    !!summaryProse && summaryProse === overallAssessment?.summary;
  const sentimentScore = callData.callerSentimentScore;
  const sentimentChange = callData.callerSentimentChange;
  /* With no score and no trend the old markup still drew a dash, two "N/A"s and
     an empty track — noise where there is simply nothing to report. */
  const hasSentiment =
    (sentimentScore !== undefined && sentimentScore !== null && !isNaN(sentimentScore)) ||
    !!sentimentChange;

  return (
  <div className="space-y-6">
    {/* One grid rather than a 4-row and a 3-row column side by side, which left
        the right half of the panel empty. */}
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Call Details
      </h3>
      <dl className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
        <Fact label="Agent" value={callData?.ConversationAnalytics?.Agent || callData.agent} />
        <Fact label="Campaign" value={callData?.ConversationAnalytics?.Cust || callData.customer} />
        <Fact label="Queue" value={callData?.ConversationAnalytics?.Queue || callData.queue} />
        <Fact label="Language" value={callData?.ConversationAnalytics?.LanguageCode || callData.lang} />
        <Fact label="Topic" value={callData.summary_topic || callData?.ConversationAnalytics?.ContactSummary?.Topic} />
        <Fact label="Product" value={callData.summary_product || callData?.ConversationAnalytics?.ContactSummary?.Product} />
        <Fact label="Resolved">
          <ResolvedPill value={resolved} />
        </Fact>
      </dl>
    </div>

    {/* Generated prose sits on its own surface so it is not mistaken for a
        recorded fact from the grid above. */}
    {(summaryProse || genAiSummary.length > 0) && (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <FaFileAlt className="text-cloudza-600" />
          Summary
        </h3>
        {summaryProse && (
          <div className="mb-4 text-left">
            <p className="text-sm text-gray-700 leading-relaxed">{summaryProse}</p>
            {proseFromAssessment && overallAssessment?.confidence && (
              <p className="mt-1 text-xs text-gray-500">
                Confidence: {overallAssessment.confidence}
              </p>
            )}
          </div>
        )}
        {genAiSummary.length > 0 && (
          <dl className="space-y-3 text-left">
            {genAiSummary
              .filter((entry) => {
                const normalized = (entry.label ?? '')
                  .toString()
                  .trim()
                  .toLowerCase()
                  .replace(/:$/, '');
                if (['validations', 'transcription', 'contact summary', 'topic', 'product', 'resolved', 'actions', 'overall assessment', 'overall_assessment', 'overallassessment'].includes(normalized)) return false;
                // A failed generation stores this literal placeholder as the
                // value; and a 'summary' entry that matches the prose already
                // rendered above would just repeat it.
                const value = (entry.value ?? '').toString().trim();
                if (value === '' || value === SUMMARY_FAILED_PLACEHOLDER) return false;
                if (normalized === 'summary' && value === (summaryProse ?? '').toString().trim()) return false;
                return true;
              })
              .map((entry, i) => {
                const label = (entry.label ?? '').toString().trim();
                const normalizedLabel = label.toLowerCase().replace(/:$/, '');

                if (normalizedLabel === 'summary') {
                  return (
                    <div key={`summary-${i}`} className="text-sm text-gray-900 whitespace-pre-line">
                      {entry.value}
                    </div>
                  );
                }

                return (
                  <div key={`${normalizedLabel || 'entry'}-${i}`}>
                    <dt className="text-sm font-medium text-gray-500">{label}</dt>
                    <dd className="text-sm text-gray-900 whitespace-pre-line text-left">{entry.value}</dd>
                  </div>
                );
              })}
          </dl>
        )}
      </div>
    )}

    {hasSentiment && (
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Sentiment Analysis
        </h3>
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-3">
              <SentimentIcon score={sentimentScore} size="lg" />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Customer Sentiment
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  <SentimentText score={sentimentScore} showScore={false} />
                </p>
              </div>
            </div>

            {sentimentChange && (
              <div className="flex items-center gap-3">
                <TrendIcon trend={sentimentChange} size="lg" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Sentiment Change
                  </p>
                  <p className="text-sm font-semibold capitalize text-gray-900">{sentimentChange}</p>
                </div>
              </div>
            )}
          </div>

          {sentimentScore !== undefined && sentimentScore !== null && !isNaN(sentimentScore) && (
            <div className="mt-4">
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Sentiment Score
                </span>
                <span className="text-sm font-semibold text-gray-900">{sentimentScore}</span>
              </div>
              <SentimentBar score={sentimentScore} height="h-2" />
              {/* The bar carried no scale, so a filled width meant nothing on its own. */}
              <div className="mt-1 flex justify-between text-[10px] text-gray-400">
                <span>Negative</span>
                <span>Neutral</span>
                <span>Positive</span>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
  );
};

// Transcript Tab
const TranscriptTab = ({ callData, speakerLabels, audioRef, transcriptRef, onAudioPlayTimeUpdate, deriveSentimentScore, callKey, swapAction }) => {
  const hasTranslations = (callData?.SpeechSegments || []).some(s => s.TranslatedText);
  const [globalEnglish, setGlobalEnglish] = useState(false);

  return (
  <div className="space-y-4">
    {callData.transcript && (
      <div className="card">
        <h4 className="text-sm font-bold text-gray-900 mb-2">Transcription</h4>
        {/* <TranscriptionText text={callData.transcript} /> */}
      </div>
    )}

    {swapAction && <div className="flex justify-end">{swapAction}</div>}

    {/* Playback toolbar — custom player and translation toggle on one row */}
    <div className="flex items-center gap-3 flex-wrap bg-[#fafafc] border border-gray-200 rounded-xl p-2.5">
      {callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo?.MediaFileUri && (
        <AudioPlayer
          audioRef={audioRef}
          src={callData.ConversationAnalytics.SourceInformation[0].TranscribeJobInfo.MediaFileUri}
          onTimeUpdate={onAudioPlayTimeUpdate}
          onLoadedMetadata={() => {
            const saved = sessionStorage.getItem(`audioTime_${callKey}`);
            if (saved && audioRef.current) {
              audioRef.current.currentTime = parseFloat(saved);
              sessionStorage.removeItem(`audioTime_${callKey}`);
            }
          }}
        />
      )}
      {hasTranslations && (
        <button
          className={`btn-outline !py-1 !px-3 !text-xs flex-shrink-0 ${globalEnglish ? '!bg-gray-100' : ''}`}
          onClick={() => setGlobalEnglish(v => !v)}
        >
          {globalEnglish ? 'Show Zulu' : 'Show English'}
        </button>
      )}
    </div>

    <div ref={transcriptRef} className="space-y-3">
      {(callData?.SpeechSegments || []).map((s, i) => {
        const segmentScore = deriveSentimentScore
          ? deriveSentimentScore(s)
          : (s.SentimentIsPositive - s.SentimentIsNegative);
        return (
          <TranscriptSegment
            key={i}
            name={speakerLabels[s.SegmentSpeaker] || s.SegmentSpeaker}
            allSegments={s.WordConfidence || []}
            segmentStart={s.SegmentStartTime}
            text={s.DisplayText}
            translatedText={s.TranslatedText || ''}
            globalEnglish={globalEnglish}
            onClick={(e) => {
              const t = e.target.dataset.currenttime;
              if (t && audioRef.current) audioRef.current.currentTime = t;
            }}
            highlightLocations={[
              ...(s.EntitiesDetected || []).map((e) => ({
                start: e.BeginOffset,
                end: e.EndOffset,
                fn: (match, key, start, end, oStart, oEnd) => (
                  <TranscriptOverlay
                    key={key}
                    colour={getEntityColor(e.Type)}
                    visuallyHidden={`Entity - ${e.Type}`}
                    data-start={start}
                    data-end={end}
                    data-offset-start={oStart}
                    data-offset-end={oEnd}
                    content={match}
                  />
                ),
              })),
            ]}
            score={segmentScore}
            interruption={s.SegmentInterruption}
            ivr={s.IVRSegment}
            categoryList={s.CategoriesDetected}
          />
        );
      })}

      {!callData?.SpeechSegments?.length && (
        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
          {callData.transcript || 'Transcript not available for this call.'}
        </p>
      )}
    </div>
  </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ callData, speakerLabels }) => {
  const sentimentData = callData?.ConversationAnalytics?.SentimentTrends;
  const speakerTime = callData?.ConversationAnalytics?.SpeakerTime;

  if (!sentimentData || !speakerTime) {
    return (
      <div className="text-center py-8">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No detailed analytics available</h3>
      </div>
    );
  }

  const speakerTimeData = Object.entries(speakerTime).map(([key, value]) => ({
    value: value.TotalTimeSecs,
    label: speakerLabels[key] || key,
    channel: key
  }));

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <SentimentChart data={sentimentData} speakerOrder={speakerLabels} />
      </div>
      <div className="bg-gray-50 rounded-lg p-4">
        <SpeakerTimeChart data={speakerTimeData} />
      </div>
    </div>
  );
};

// QA Tab

const QATab = ({ callKey, callData, onRefresh, setAlert, userEmail, username, campaign, currentTemplate, summary, transcript, onRefr, onSubmit, history }) => {
  // A compliance-only scorecard asks no QA questions, so "Quality Assessment" misnames it.
  const complianceOnly = isComplianceOnlyScorecard(callData);
  const assessmentLabel = complianceOnly ? 'Compliance Assessment' : 'Quality Assessment';
  const [makeEdit, setMakeEdit] = useState(false)
  const [editableCompliance, setEditableCompliance] = useState({});
  const [editableQuality, setEditableQuality] = useState({});
  const [editableConduct, setEditableConduct] = useState({});
  // Reviewer corrections to the applicability pre-check steps: { [question]: { [step]: {...} } }.
  // Holds ONLY what the reviewer edited, never a seeded copy of every step - the API stamps
  // overriddenBy on each step it receives, so sending untouched ones would record the gate's own
  // findings as human decisions. Keeping only the edits makes that structural rather than a rule
  // some later save path could forget.
  const [editableSteps, setEditableSteps] = useState({});
  const [isAssessed, setIsAssessed] = useState(callData?.ConversationAnalytics?.AssessDetails?.assessed || false);
  const [markingAssessed, setMarkingAssessed] = useState(false);
  // Non-sales: an unsuccessful call a QA manager marks so it is left out of scores. Read from the
  // loaded call, which is refetched after each change. Independent of assessed - it locks nothing.
  const isNonSales = isNonSalesCall(callData);
  const nonSalesDetails = nonSalesDetailsOf(callData);
  const [showNonSalesModal, setShowNonSalesModal] = useState(false);
  const [savingNonSales, setSavingNonSales] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  // One slot for whichever confirmation is pending — native window.confirm()
  // doesn't fit the app's design language, so this drives the themed
  // ConfirmationModal instead. { title, message, onConfirm }
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Entering Override mode itself is treated as "dirty" — a person opening
  // the edit form clearly means to change something, and waiting for an
  // actual field diff before warning would let a first, unsaved edit slip
  // through if they then navigate away immediately.
  const blocker = useUnsavedChangesGuard(makeEdit);
  const { mutate } = useSWRConfig();
  const isSuperAdmin = useIsSuperAdmin();
  const isCallAgent = useIsCallAgent();
  const [isDisputed, setIsDisputed] = useState(callData?.ConversationAnalytics?.DisputeDetails?.disputed || false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeStatus, setDisputeStatus] = useState(callData?.ConversationAnalytics?.DisputeDetails?.status || null);
  const [disputeOutcome, setDisputeOutcome] = useState(callData?.ConversationAnalytics?.DisputeDetails?.outcome || null);
  const [disputeResolutionReason, setDisputeResolutionReason] = useState(callData?.ConversationAnalytics?.DisputeDetails?.resolutionReason || null);
  const [showResolveDisputeModal, setShowResolveDisputeModal] = useState(false);
  // Which outcome the currently-open ResolveDisputeModal will submit - set by whichever
  // button opened it ("No Change Needed" vs finishing a "Change Assessment" edit), never
  // chosen inside the modal itself.
  const [resolveOutcome, setResolveOutcome] = useState(null);
  // True once an Override save has actually succeeded during a "Change Assessment" reopen -
  // "Finish Resolving Dispute" (outcome: changed) only appears once this is true, so a
  // reviewer can't submit "changed" without having actually made and saved a change.
  const [disputeEditSaved, setDisputeEditSaved] = useState(false);

  const validationsData = callData?.ConversationAnalytics?.Summary?.Validations || {};
  const complianceItems = [];
  const qualityItems = [];
  const conductItems = [];

  if (validationsData && Object.keys(validationsData).length > 0) {
    Object.entries(validationsData).forEach(([key, value]) => {
      // Skip the Scores object - it's not a validation question
      if (key === 'Scores') {
        return;
      }

      try {
        const parsed = JSON.parse(value);

        // Use the 'type' field if available (v2.0+), otherwise fall back to legacy detection
        if (parsed.type) {
          // v2.0: Use explicit type field injected by backend.
          // Conduct must be matched before the quality fallback - it is pass/fail and non-scoring,
          // so rendering it as Quality shows it with a meaningless points score.
          if (parsed.type === ASSESSMENT_TYPE.COMPLIANCE) {
            complianceItems.push({ key, parsed });
          } else if (parsed.type === ASSESSMENT_TYPE.CONDUCT) {
            conductItems.push({ key, parsed });
          } else {
            qualityItems.push({ key, parsed });
          }
        } else {
          // Legacy: Detect based on presence of score or status fields
          if (parsed.score || !parsed.status) {
            qualityItems.push({ key, parsed });
          } else {
            complianceItems.push({ key, parsed });
          }
        }
      } catch (e) {
        // Skip invalid entries
        console.error(`Failed to parse validation ${key}:`, e);
      }
    });
  }

  useEffect(() => {
    // Never reseed while the user is actively editing — a background refresh
    // must not clobber in-progress override values.
    if (makeEdit) return;
    if (complianceItems.length > 0) {
      const initialEditValues = {};
      complianceItems.forEach(({ key, parsed }) => {
        const isYesNo = parsed.answer !== undefined;

        initialEditValues[key] = {
          question: key,
          reason: parsed.reason || '',
          ...(isYesNo
            // Seed a gated-out verdict as n/a rather than 'no', so opening the editor does not
            // silently turn a question that did not apply into a compliance failure on save.
            ? { answer: isNotScored(parsed) ? 'n/a' : (answerOf(parsed) || 'no') }
            : { compliance: parsed.status || '' }
          )
        };
      });
      setEditableCompliance(initialEditValues);
    }
    if (qualityItems.length > 0) {
      const initialEditValues = {};
      qualityItems.forEach(({ key, parsed }) => {
        const isYesNo = parsed.answer !== undefined && parsed.score === undefined;

        initialEditValues[key] = {
          question: key,
          reason: parsed.reason || '',
          ...(isYesNo
            ? { answer: answerOf(parsed) || 'no' }
            : { score: parsed.score || '' }
          )
        };
      });
      setEditableQuality(initialEditValues);
    }
    if (conductItems.length > 0) {
      const initialEditValues = {};
      conductItems.forEach(({ key, parsed }) => {
        // Conduct is always yes/no - it carries no weighting and no numeric score.
        // A verdict that was never scored must seed as 'n/a', or opening edit mode would show
        // "No" preselected and saving would silently turn a question that did not apply into a
        // failure.
        initialEditValues[key] = {
          question: key,
          reason: parsed.reason || '',
          answer: isNotScored(parsed) ? 'n/a' : (answerOf(parsed) || 'no')
        };
      });
      setEditableConduct(initialEditValues);
    }
    // Depend on the payload identity (not just item counts) so a refresh that
    // changes answers without changing counts still reseeds the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callData, makeEdit, complianceItems.length, qualityItems.length, conductItems.length]);

  const handleComplianceChange = (key, field, value) => {
    setEditableCompliance(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleQualityChange = (key, field, value) => {
    setEditableQuality(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleConductChange = (key, field, value) => {
    setEditableConduct(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleStepChange = (key, stepNumber, field, value) => {
    setEditableSteps(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [stepNumber]: { ...(prev[key]?.[stepNumber] || {}), [field]: value }
      }
    }));
  };

  // Attach `steps` only when the reviewer actually corrected something on that question. A question
  // nobody touched keeps exactly the payload it had before step correction existed.
  const withStepCorrections = (change) => {
    const edits = editableSteps[change.question] || {};
    const steps = Object.entries(edits).map(([step, fields]) => ({ step: Number(step), ...fields }));
    return steps.length > 0 ? { ...change, steps } : change;
  };

  const getComplianceChanges = () => {
    return Object.values(editableCompliance).map(withStepCorrections);
  };

  const getQualityChanges = () => {
    let x = Object.values(editableQuality)
    return x
  };

  const getConductChanges = () => {
    return Object.values(editableConduct).map(withStepCorrections);
  };

  const handleSaveChanges = async () => {
    let body = [...getComplianceChanges(), ...getQualityChanges(), ...getConductChanges()]
    const changes = {changes : body, user: userEmail, username: username || userEmail}

    setSavingChanges(true);
    try {
      const result = await override(callKey, changes);
      setMakeEdit(false);
      await mutate(`/get/${callKey}`);
      // A save made while reopened for dispute review is what unlocks "Finish Resolving
      // Dispute" (outcome: changed) - see the isDisputed/disputeStatus gating on that button.
      if (isDisputed && disputeStatus === 'pending' && !isAssessed) {
        setDisputeEditSaved(true);
      }
      // The override skips a change it cannot apply but still returns 200, listing why in
      // `warnings`. Reporting that as saved is what made rejected edits look like they had stuck.
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      setAlert(warnings.length > 0
        ? {
            variant: 'warning',
            heading: 'Some changes were not saved',
            text: warnings.join(' ')
          }
        : {
            variant: 'success',
            heading: 'Success',
            text: 'Your changes have been saved.'
          });

    } catch (error) {
      console.error('Error saving changes:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: error.response?.data?.error || 'Failed to save changes'
      });
    } finally {
      setSavingChanges(false);
    }
  };

  const handleMarkAssessed = () => {
    if (isAssessed) {
      setAlert({
        variant: 'warning',
        heading: 'Already Assessed',
        text: 'This call has already been marked as assessed.'
      });
      return;
    }

    setConfirmDialog({
      title: 'Mark as Assessed',
      message: 'Are you sure you want to mark this call as assessed? This action cannot be undone and will prevent any future edits.',
      onConfirm: doMarkAssessed,
    });
  };

  const doMarkAssessed = async () => {
    setConfirmDialog(null);
    setMarkingAssessed(true);
    try {
      await markAssessed(callKey);
      setIsAssessed(true);
      setAlert({
        variant: 'success',
        heading: 'Success',
        text: 'Call has been marked as assessed and is now locked from further edits.'
      });
    } catch (error) {
      console.error('Error marking as assessed:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: error.response?.data?.error || 'Failed to mark call as assessed'
      });
    } finally {
      setMarkingAssessed(false);
    }
  };

  const handleUnmarkAssessed = () => {
    if (!isAssessed) {
      setAlert({
        variant: 'warning',
        heading: 'Not Assessed',
        text: 'This call is not currently marked as assessed.'
      });
      return false;
    }

    setConfirmDialog({
      title: 'Unmark as Assessed',
      message: 'Are you sure you want to unmark this call as assessed? This will allow edits and refreshes again.',
      onConfirm: doUnmarkAssessed,
    });
  };

  // Returns true on a successful unmark, false otherwise, so callers (e.g.
  // handleStartDisputeChange) can chain follow-up UI state off a real success rather than
  // guessing from state that React may not have committed yet.
  const doUnmarkAssessed = async () => {
    setConfirmDialog(null);
    setMarkingAssessed(true);
    try {
      await unmarkAssessed(callKey);
      setIsAssessed(false);
      await mutate(`/get/${callKey}`);
      setAlert({
        variant: 'success',
        heading: 'Success',
        text: 'Call has been unmarked as assessed and can now be edited.'
      });
      // Data is revalidated in place via mutate above — a full page reload dropped
      // the user back at the top of the page instead of leaving them where
      // they were on the call.
      return true;
    } catch (error) {
      console.error('Error unmarking as assessed:', error);
      let errorMessage = 'Failed to unmark call as assessed';
      if (error.message?.includes('403') || error.message?.includes('Unauthorized')) {
        errorMessage = 'Unauthorized: Only SuperAdmin users can unmark any assessed call; QAAgent/Admin users may only unmark a call that is currently disputed';
      }
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: errorMessage
      });
      return false;
    } finally {
      setMarkingAssessed(false);
    }
  };

  // Errors are thrown back to NonSalesModal, which shows them and keeps the reason.
  const handleMarkNonSales = async (reason) => {
    await markNonSales(callKey, reason);
    await mutate(`/get/${callKey}`);
    setShowNonSalesModal(false);
    setAlert({
      variant: 'success',
      heading: 'Marked as non-sales',
      text: 'This call is marked non-sales and can be left out of scores.'
    });
  };

  const handleMarkSales = () => {
    setConfirmDialog({
      title: 'Mark as Sales',
      message: 'This call will count as a sales call again and be included in scores.',
      onConfirm: doMarkSales
    });
  };

  const doMarkSales = async () => {
    setConfirmDialog(null);
    setSavingNonSales(true);
    try {
      await unmarkNonSales(callKey);
      await mutate(`/get/${callKey}`);
      setAlert({ variant: 'success', heading: 'Marked as sales', text: 'This call counts as a sales call again.' });
    } catch (error) {
      console.error('Error marking call as sales:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: error.message?.includes('403')
          ? 'Unauthorized: only QA agents, admins and super admins can change the non-sales flag.'
          : 'Failed to mark the call as sales.'
      });
    } finally {
      setSavingNonSales(false);
    }
  };

  const handleDispute = async (reason) => {
    await dispute(callKey, reason);
    setIsDisputed(true);
    setDisputeStatus('pending');
    setShowDisputeModal(false);
    setAlert({
      variant: 'success',
      heading: 'Dispute Submitted',
      text: 'Your dispute has been recorded and will be reviewed.'
    });
  };

  const handleNoChangeNeeded = () => {
    setResolveOutcome('upheld');
    setShowResolveDisputeModal(true);
  };

  // "Change Assessment": reopen the call and go straight into edit mode in one click, no
  // separate "Override" click needed. Reuses the exact same unmark endpoint/handler QA/Admin's
  // scoped reopen already uses - nothing new is unlocked here beyond what handleUnmarkAssessed
  // already permits, this just gives it a dispute-specific entry point and label.
  const handleStartDisputeChange = () => {
    if (!isAssessed) {
      setAlert({
        variant: 'warning',
        heading: 'Not Assessed',
        text: 'This call is not currently marked as assessed.'
      });
      return;
    }
    setConfirmDialog({
      title: 'Change Assessment',
      message: 'Are you sure you want to unmark this call as assessed? This will allow edits and refreshes again.',
      onConfirm: async () => {
        const success = await doUnmarkAssessed();
        if (success) {
          setMakeEdit(true);
        }
      },
    });
  };

  const handleResolveDispute = async (reason) => {
    const outcome = resolveOutcome;
    const response = await resolveDispute(callKey, outcome, reason);
    setDisputeStatus('resolved');
    setDisputeOutcome(outcome);
    setDisputeResolutionReason(reason);
    if (response?.assessed) {
      setIsAssessed(true);
    }
    setShowResolveDisputeModal(false);
    setAlert({
      variant: 'success',
      heading: 'Dispute Resolved',
      text: 'The dispute has been marked as reviewed.'
    });
  };

return (
  <div className="space-y-6">
    <ConfirmationModal
      open={!!confirmDialog}
      title={confirmDialog?.title}
      message={confirmDialog?.message}
      onClose={() => setConfirmDialog(null)}
      onConfirm={confirmDialog?.onConfirm}
    />
    <ConfirmationModal
      open={blocker.state === 'blocked'}
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to leave?"
      confirmText="Leave"
      onClose={() => blocker.reset()}
      onConfirm={() => blocker.proceed()}
    />
    <InfoCard
      id="qa-guidelines"
      title={`${assessmentLabel} Guidelines`}
      items={[
        'Listen to the complete call recording before assessment',
        complianceOnly
          ? 'Evaluate each compliance requirement objectively against the transcript'
          : 'Evaluate each criterion objectively using the scoring rubric',
        'Provide specific feedback and examples where applicable',
        'Consider both agent performance and customer satisfaction',
      ]}
    />
    {isAssessed && (
      <div className="bg-coral-50 border border-coral-200 rounded-xl p-4">
        <div className="flex items-center">
          <FaCheckCircle className="text-coral-600 mr-2" />
          <div>
            <p className="text-sm font-medium text-coral-900">This call has been marked as assessed</p>
            <p className="text-xs text-coral-700 mt-1">No further edits or refreshes are allowed</p>
          </div>
        </div>
      </div>
    )}

    {isNonSales && (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="non-sales-banner">
        <div className="flex items-start">
          <FaExclamationTriangle className="text-amber-600 mr-2 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">This call is marked non-sales</p>
            <p className="text-xs text-amber-800 mt-1">
              Marked by {nonSalesDetails.markedBy || 'unknown'}
              {nonSalesDetails.markedAt ? ` on ${new Date(nonSalesDetails.markedAt).toLocaleString()}` : ''}
              {' '}- it can be left out of scores.
            </p>
            {nonSalesDetails.reason && (
              <p className="text-xs text-amber-800 mt-1">Reason: {nonSalesDetails.reason}</p>
            )}
          </div>
        </div>
      </div>
    )}

    <div className="flex flex-wrap items-center justify-between gap-y-2">
      <h3 className="text-sm font-bold text-gray-900">{assessmentLabel}</h3>
      <div className="flex flex-wrap items-center gap-2">
        {isCallAgent ? (
          <>
            {isAssessed && !isDisputed && (
              <button
                className='btn-danger'
                onClick={() => setShowDisputeModal(true)}
              >
                <FaExclamationTriangle /> Dispute
              </button>
            )}
            {isAssessed && isDisputed && disputeStatus !== 'resolved' && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-yellow-100 text-yellow-800">
                <FaExclamationTriangle className="mr-1.5" />
                Disputed &mdash; pending review
              </span>
            )}
            {isAssessed && isDisputed && disputeStatus === 'resolved' && (
              <div className="text-right">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${disputeOutcome === 'changed' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                  <FaCheckCircle className="mr-1.5" />
                  Dispute Resolved &mdash; {disputeOutcome === 'changed' ? 'Assessment Changed' : 'Assessment Upheld'}
                </span>
                {disputeResolutionReason && (
                  <p className="text-xs text-gray-500 mt-1 max-w-xs">{disputeResolutionReason}</p>
                )}
              </div>
            )}
            {/* Not yet assessed: nothing to dispute, so Call Agents see no controls here */}
          </>
        ) : (
          <>
            {!isAssessed && (
              <>
                <RefreshButton
                  onRefresh={onRefresh}
                  setAlert={setAlert}
                  showTemplateDropdown={true}
                  campaign={campaign}
                  currentTemplate={currentTemplate}
                  className="text-sm"
                />
                <button className='btn-secondary' onClick={() => setMakeEdit(true)}>
                  <FaPen /> Override
                </button>
                {/* While reopened as part of an active dispute review, Mark as Assessed is
                    hidden - re-locking has to go through Finish Resolving Dispute (which
                    requires an actual saved change) or No Change Needed, not a bare re-mark
                    that would silently skip recording any decision on the dispute at all. */}
                {!(isDisputed && disputeStatus === 'pending') && (
                  <button
                    className='btn-primary'
                    onClick={handleMarkAssessed}
                    disabled={markingAssessed}
                  >
                    <><FaCheckCircle /> {markingAssessed ? 'Marking...' : 'Mark as Assessed'}</>
                  </button>
                )}
                {isDisputed && disputeStatus === 'pending' && !disputeEditSaved && (
                  <span className="text-sm text-gray-500 italic">
                    Make your changes below, then click Save Changes to continue resolving this dispute.
                  </span>
                )}
                {isDisputed && disputeStatus === 'pending' && disputeEditSaved && (
                  <button
                    className='btn-secondary bg-cyan-600 hover:bg-cyan-700 text-white'
                    onClick={() => { setResolveOutcome('changed'); setShowResolveDisputeModal(true); }}
                  >
                    Finish Resolving Dispute
                  </button>
                )}
              </>
            )}
            {isAssessed && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-coral-100 text-coral-800">
                  <FaCheckCircle className="mr-1.5" />
                  Assessed & Locked
                </span>
                {isSuperAdmin && (
                  <button
                    className='btn-danger'
                    onClick={handleUnmarkAssessed}
                    disabled={markingAssessed}
                  >
                    <FaTimes /> {markingAssessed ? 'Unmarking...' : 'Unmark as Assessed'}
                  </button>
                )}
                {isDisputed && disputeStatus === 'pending' && (
                  <>
                    <button
                      className='btn-secondary'
                      onClick={handleNoChangeNeeded}
                    >
                      No Change Needed
                    </button>
                    <button
                      className='btn-secondary bg-orange-600 hover:bg-orange-700 text-white'
                      onClick={handleStartDisputeChange}
                      disabled={markingAssessed}
                    >
                      {markingAssessed ? 'Reopening...' : 'Change Assessment'}
                    </button>
                  </>
                )}
              </div>
            )}
            {isDisputed && disputeStatus === 'resolved' && (
              <span className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${disputeOutcome === 'changed' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                <FaCheckCircle className="mr-1.5" />
                Dispute Resolved &mdash; {disputeOutcome === 'changed' ? 'Changed' : 'Upheld'}
              </span>
            )}
            {/* Non-sales is independent of assessed, so it is offered in either state. */}
            {isNonSales ? (
              <>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-amber-100 text-amber-800">
                  Non-sales
                </span>
                <button className='btn-secondary' onClick={handleMarkSales} disabled={savingNonSales}>
                  {savingNonSales ? 'Saving...' : 'Mark as Sales'}
                </button>
              </>
            ) : (
              <button className='btn-secondary' onClick={() => setShowNonSalesModal(true)}>
                Mark as Non-sales
              </button>
            )}
          </>
        )}
      </div>
    </div>

    <NonSalesModal
      open={showNonSalesModal}
      onClose={() => setShowNonSalesModal(false)}
      onSave={handleMarkNonSales}
    />

    <DisputeModal
      open={showDisputeModal}
      onClose={() => setShowDisputeModal(false)}
      onSave={handleDispute}
    />

    <ResolveDisputeModal
      open={showResolveDisputeModal}
      onClose={() => setShowResolveDisputeModal(false)}
      onSave={handleResolveDispute}
      outcome={resolveOutcome}
      disputeReason={callData?.ConversationAnalytics?.DisputeDetails?.reason}
    />

    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
      {/* self-start keeps this card at its content height — stretching it to match
          the assessment list left a large dead area under the query box. */}
      <div className="card md:col-span-2 self-start">
        {/* <h4 className="text-md font-medium text-gray-900 mb-3">Current Scores</h4>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Overall QA Score</dt>
            <dd className={`text-sm font-medium ${
              (callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score) >= 80 ? 'text-green-600' :
              (callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score) >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score ?? 0}/100
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Compliance Status</dt>
            <dd className={`text-sm font-medium ${
              (callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'text-green-600' : 'text-red-600'
            }`}>
              {(callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'Pass' : 'Fail'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Assessment Status</dt>
            <dd className="text-sm text-gray-500">
              {(callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score) ? 'Completed' : 'Pending'}
            </dd>
          </div>

          {callData.ConversationAnalytics?.Rating?.CriticalFail && (
            <div className="pt-3 mt-3 border-t border-gray-200">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <dt className="text-sm font-semibold text-red-700 flex items-center gap-2">
                  <FaExclamationTriangle className="flex-shrink-0" /> Critical Fail
                </dt>
                <dd className="text-xs text-red-700 mt-1">
                  {callData.ConversationAnalytics?.Summary?.Scores?.criticalFailReason ||
                   'One or more critical requirements were not met'}
                </dd>
              </div>
            </div>
          )}
          {isAssessed && (
            <>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <dt className="text-sm text-gray-500">Assessed By</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {callData.assessedBy || callData.ConversationAnalytics?.AssessDetails?.assessedBy || '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Assessed At</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {callData.assessedAt
                    ? Formatter.Timestamp(callData.assessedAt)
                    : (callData.ConversationAnalytics?.AssessDetails?.assessedAt
                        ? Formatter.Timestamp(callData.ConversationAnalytics.AssessDetails.assessedAt)
                        : '-')}
                </dd>
              </div>
            </>
          )}
        </dl> */}
        <GenAITab 
          summary={summary}
          transcript={transcript}
          onRefr={onRefr}
          history={history}
          onSubmit={onSubmit} 
          setAlert={setAlert}
        />
      </div>

      <div className="card md:col-span-2">
        <h4 className="text-sm font-bold text-gray-900 mb-4">Assessment Details</h4>
        {(() => {
          
          if (validationsData && Object.keys(validationsData).length > 0) {
            
            return (
              <div className="space-y-6 max-h-[34rem] overflow-y-auto pr-1 -mr-1">
                {(complianceItems.length > 0 && !makeEdit) && (
                  <div>
                    <SectionHeading
                      title="Compliance"
                      items={complianceItems.map(({ parsed }) => isComplianceItemPassed(parsed))}
                    />
                    <ul className="space-y-3">
                      {complianceItems.map(({ key, parsed }) => {
                        // Resolve the non-scoring states FIRST, as the conduct section does. A
                        // gated compliance question that did not apply was never scored; rendering
                        // it through the pass/violation logic below would paint it red and make it
                        // indistinguishable from a real compliance failure.
                        const notScored = isNotScored(parsed);
                        const unchecked = isUnchecked(parsed);
                        const notApplicable = !unchecked && isNotApplicable(parsed);

                        const answer = answerOf(parsed);
                        const success = successOf(parsed);
                        const passed = isComplianceItemPassed(parsed);
                        const legacyStatus = !answer && parsed.status;
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;

                        if (!notScored) {
                          return (
                            <AssessmentCriterion
                              key={key}
                              name={key}
                              question={parsed.assessment}
                              tone={passed ? 'passed' : 'failed'}
                              statusLabel={
                                legacyStatus
                                  ? parsed.status
                                  : passed
                                  ? 'Compliant'
                                  : 'Violation'
                              }
                              critical={isCriticalFail}
                              answer={answer}
                              expected={answer ? success : undefined}
                              reason={parsed.reason}
                              evidence={parsed.evidence || {}}
                            />
                          );
                        }

                        // Not scored (N/A or unchecked): same card language as the
                        // scored criteria, with the pre-check breakdown attached.
                        return (
                          <li key={key} className={`card !p-0 overflow-hidden border-l-4 ${notApplicable ? 'border-l-gray-300' : 'border-l-amber-400'}`}>
                            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3.5">
                              <div className="min-w-0">
                                <h6 className="text-sm font-semibold capitalize text-gray-900">{key}</h6>
                                {parsed.assessment && (
                                  <p className="mt-0.5 text-xs italic text-gray-500">{parsed.assessment}</p>
                                )}
                              </div>
                              <div className="flex flex-shrink-0 items-center gap-1.5">
                                {isCriticalFail && <StatusPill tone="critical">Critical</StatusPill>}
                                {notApplicable ? (
                                  <StatusPill tone="neutral">N/A</StatusPill>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200">
                                    <FaExclamationTriangle aria-hidden="true" /> Needs review
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="mt-2 px-4 text-xs text-gray-500">
                              {notApplicable
                                ? 'This question did not apply to this call.'
                                : 'The assessor could not determine this question — review it manually.'}
                            </p>
                            {(parsed.applicabilityReason || parsed.reason) && (
                              <p className="mt-2 px-4 text-sm leading-relaxed text-gray-700">
                                {parsed.applicabilityReason || parsed.reason}
                              </p>
                            )}
                            <div className="px-4 pb-3.5">
                              <PreCheckSteps parsed={parsed} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {(complianceItems.length > 0 && makeEdit && !isAssessed) && (
                  <div>
                    <h5 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Compliance (Edit Mode)</h5>
                    <ul className="space-y-4">
                      {complianceItems.map(({ key, parsed }) => {
                        const isYesNo = parsed.answer !== undefined;
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;

                        return (
                          <li key={key} className="text-xs border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="font-medium capitalize mb-2 flex items-center justify-between">
                              <span>{key}</span>
                              {parsed.complianceViolation && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-lg font-semibold">
                                  VIOLATION
                                </span>
                              )}
                            </div>
                            {/* Display the assessment/question text */}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-2 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <div className="ml-3 space-y-2">
                              {/* Answer/Status Field */}
                              <div>
                                <label className="block text-xs font-semibold text-gray-800 mb-1">
                                  {isYesNo ? 'Answer:' : 'Status:'}
                                </label>
                                {isYesNo ? (
                                  /* Yes/No Dropdown */
                                  <>
                                    <SelectField
                                      value={editableCompliance[key]?.answer || 'no'}
                                      onChange={(e) => handleComplianceChange(key, 'answer', e.target.value)}
                                      className="select-field w-full !text-xs !py-1 !px-2"
                                      disabled={isAssessed}
                                    >
                                      <option value="yes">Yes</option>
                                      <option value="no">No</option>
                                      {/* Only a question the author gated can be marked N/A - the
                                          override API rejects it on an ungated one, and an ordinary
                                          compliance question has no notion of "did not apply". */}
                                      {parsed.isConditional && (
                                        <option value="n/a">N/A - did not apply to this call</option>
                                      )}
                                    </SelectField>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Success is determined by the 'success' field in the question.
                                      {parsed.success && ` (Expected: ${parsed.success.toUpperCase()})`}
                                    </p>
                                  </>
                                ) : (
                                  /* Legacy Passed/Failed Dropdown */
                                  <SelectField
                                    value={editableCompliance[key]?.compliance || 'passed'}
                                    onChange={(e) => handleComplianceChange(key, 'compliance', e.target.value)}
                                    className="select-field w-full !text-xs !py-1 !px-2"
                                    disabled={isAssessed}
                                  >
                                    <option value="passed">Passed</option>
                                    <option value="failed">Failed</option>
                                  </SelectField>
                                )}
                              </div>

                              {/* Reason Input */}
                              <div>
                                <label className="block text-xs font-semibold text-gray-800 mb-1">
                                  Reason:
                                </label>
                                <textarea
                                  value={editableCompliance[key]?.reason || ''}
                                  onChange={(e) => handleComplianceChange(key, 'reason', e.target.value)}
                                  className="input-field w-full !text-sm !py-1.5 !px-2.5 resize-none"
                                  rows="2"
                                  placeholder="Enter reason..."
                                  disabled={isAssessed}
                                />
                              </div>

                              {/* Correct a pre-check the gate got wrong. This records what the
                                  reviewer found - the answer above is what decides the verdict. */}
                              <PreCheckStepsEditor
                                parsed={parsed}
                                edited={editableSteps[key] || {}}
                                onChange={(step, field, value) => handleStepChange(key, step, field, value)}
                                disabled={isAssessed}
                              />
                              {stepAnswerConflict(editableSteps[key], editableCompliance[key]?.answer) && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                  ⚠ {stepAnswerConflict(editableSteps[key], editableCompliance[key]?.answer)}
                                </p>
                              )}

                              {/* Read-only evidence display */}
                              {parsed.evidence?.agent && (
                                <div className="text-gray-600">• Agent: "{parsed.evidence.agent}"</div>
                              )}
                              {parsed.evidence?.customer && (
                                <div className="text-gray-600">• Customer: "{parsed.evidence.customer}"</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    
                    {/* Preview of changes (optional, for debugging) */}
                    {/* <div className="mt-4 p-2 bg-cloudza-50 rounded-xl">
                      <h6 className="text-sm font-medium text-cloudza-800 mb-1">Changes Preview:</h6>
                      <pre className="text-sm text-cloudza-700">
                        {JSON.stringify(getComplianceChanges(), null, 2)}
                      </pre>
                    </div> */}
                  </div>
                )}

                {/* Agent Behavior and Conduct - read-only.
                    Modelled on Compliance, not Quality: conduct is pass/fail with no weighting, so
                    there is no points row, and any wrong answer is a violation regardless of
                    whether the question is marked critical. */}
                {(conductItems.length > 0 && !makeEdit) && (
                  <div>
                    <h5 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {ASSESSMENT_TYPE_LABELS[ASSESSMENT_TYPE.CONDUCT]}
                      {conductItems.filter(({ parsed }) => isUnchecked(parsed)).length > 0 && (
                        <span className="ml-2 text-xs font-normal normal-case tracking-normal text-amber-700">
                          ({conductItems.filter(({ parsed }) => isUnchecked(parsed)).length} unchecked)
                        </span>
                      )}
                    </h5>
                    <ul className="space-y-3">
                      {conductItems.map(({ key, parsed }) => {
                        // Resolve the non-scoring states FIRST. A question that did not apply, or
                        // that the assessor could not determine, was never scored - rendering it
                        // through the pass/violation logic below would paint it red and make it
                        // indistinguishable from a real failure, which is the whole problem this
                        // feature exists to fix.
                        const notScored = isNotScored(parsed);
                        const unchecked = isUnchecked(parsed);
                        const notApplicable = !unchecked && isNotApplicable(parsed);

                        const answer = answerOf(parsed);
                        const success = successOf(parsed);
                        const passed = parsed.passed !== undefined ? parsed.passed : (answer === success);
                        const isViolation = !notScored && !passed;
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;

                        if (!notScored) {
                          return (
                            <AssessmentCriterion
                              key={key}
                              name={key}
                              question={parsed.assessment}
                              tone={passed ? 'passed' : 'failed'}
                              statusLabel={passed ? 'Pass' : 'Violation'}
                              critical={isCriticalFail}
                              answer={answer}
                              expected={answer ? success : undefined}
                              reason={parsed.reason}
                              evidence={parsed.evidence || {}}
                            />
                          );
                        }

                        // Not scored (N/A or unchecked): same card language as the
                        // scored criteria, with the pre-check breakdown attached.
                        return (
                          <li key={key} className={`card !p-0 overflow-hidden border-l-4 ${notApplicable ? 'border-l-gray-300' : 'border-l-amber-400'}`}>
                            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3.5">
                              <div className="min-w-0">
                                <h6 className="text-sm font-semibold capitalize text-gray-900">{key}</h6>
                                {parsed.assessment && (
                                  <p className="mt-0.5 text-xs italic text-gray-500">{parsed.assessment}</p>
                                )}
                              </div>
                              <div className="flex flex-shrink-0 items-center gap-1.5">
                                {isCriticalFail && <StatusPill tone="critical">Critical</StatusPill>}
                                {notApplicable ? (
                                  <StatusPill tone="neutral">N/A</StatusPill>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200">
                                    <FaExclamationTriangle aria-hidden="true" /> Needs review
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="mt-2 px-4 text-xs text-gray-500">
                              {notApplicable
                                ? 'This question did not apply to this call.'
                                : 'The assessor could not determine this question — review it manually.'}
                            </p>
                            {(parsed.applicabilityReason || parsed.reason) && (
                              <p className="mt-2 px-4 text-sm leading-relaxed text-gray-700">
                                {parsed.applicabilityReason || parsed.reason}
                              </p>
                            )}

                            {/* The gate's working, step by step. Showing the authored step
                                text beside its result is what makes a mis-worded pre-check
                                obvious — an instruction-shaped step next to a verdict that
                                contradicts it reads wrong at a glance. */}
                            {gateSteps(parsed).length > 0 && (
                              <div className="mt-3 border-t border-gray-100 px-4 py-3">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Pre-checks</div>
                                <ol className="space-y-2">
                                  {gateSteps(parsed).map(step => (
                                    <li
                                      key={step.number}
                                      className={`border rounded-lg p-2.5 ${
                                        step.number === parsed.applicabilityFailedStep
                                          ? 'border-gray-200 bg-gray-50'
                                          : 'border-gray-100'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <span className="text-xs text-gray-700">
                                          <span className="font-medium">Step {step.number}.</span>{' '}
                                          {step.text || <em className="text-gray-400">no step text stored</em>}
                                        </span>
                                        {step.result === 'true' ? (
                                          <span className="shrink-0 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-semibold">TRUE</span>
                                        ) : step.result === 'false' ? (
                                          <span className="shrink-0 text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-semibold">FALSE</span>
                                        ) : step.result === 'unknown' ? (
                                          <span className="shrink-0 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold">UNKNOWN</span>
                                        ) : (
                                          <span className="shrink-0 text-xs text-gray-400">not reached</span>
                                        )}
                                      </div>
                                      {step.reason && (
                                        <div className="text-xs text-gray-600 mt-1">{step.reason}</div>
                                      )}
                                      {step.agent && (
                                        <div className="text-xs italic text-gray-600 mt-1"><span className="not-italic font-semibold text-gray-500">Agent:</span> "{step.agent}"</div>
                                      )}
                                      {step.customer && (
                                        <div className="text-xs italic text-gray-600 mt-1"><span className="not-italic font-semibold text-gray-500">Customer:</span> "{step.customer}"</div>
                                      )}
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}

                            {/* Fallback for verdicts written before the per-step breakdown
                                existed — they carry only the failing step number. */}
                            {gateSteps(parsed).length === 0 && parsed.applicabilityFailedStep && (
                              <p className="mt-2 px-4 text-xs text-gray-500">
                                Pre-check step {parsed.applicabilityFailedStep} did not hold
                              </p>
                            )}
                            <div className="pb-3.5" />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Agent Behavior and Conduct - edit mode */}
                {(conductItems.length > 0 && makeEdit && !isAssessed) && (
                  <div>
                    <h5 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {ASSESSMENT_TYPE_LABELS[ASSESSMENT_TYPE.CONDUCT]} (Edit Mode)
                    </h5>
                    <ul className="space-y-4">
                      {conductItems.map(({ key, parsed }) => {
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;
                        const unchecked = isUnchecked(parsed);
                        const notApplicable = !unchecked && isNotApplicable(parsed);

                        return (
                          <li key={key} className="text-xs border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="font-medium capitalize mb-2 flex items-center justify-between">
                              <span>{key}</span>
                              {notApplicable ? (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg font-semibold">
                                  N/A
                                </span>
                              ) : unchecked ? (
                                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-lg font-semibold">
                                  <FaExclamationTriangle aria-hidden="true" /> UNCHECKED
                                </span>
                              ) : parsed.conductViolation && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-lg font-semibold">
                                  VIOLATION
                                </span>
                              )}
                            </div>
                            {(notApplicable || unchecked) && (parsed.applicabilityReason || parsed.reason) && (
                              <div className="text-xs text-gray-500 mb-2 ml-3">
                                {notApplicable
                                  ? `Not assessed: ${parsed.applicabilityReason || parsed.reason}`
                                  : `Could not be determined: ${parsed.applicabilityReason || parsed.reason}`}
                              </div>
                            )}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-2 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <div className="ml-3 space-y-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-800 mb-1">
                                  Answer:
                                </label>
                                <SelectField
                                  value={editableConduct[key]?.answer || 'no'}
                                  onChange={(e) => handleConductChange(key, 'answer', e.target.value)}
                                  className="select-field w-full !text-xs !py-1 !px-2"
                                  disabled={isAssessed}
                                >
                                  <option value="yes">Yes</option>
                                  <option value="no">No</option>
                                  <option value="n/a">N/A - did not apply to this call</option>
                                </SelectField>
                                <p className="text-xs text-gray-500 mt-1">
                                  Success is determined by the 'success' field in the question.
                                  {parsed.success && ` (Expected: ${parsed.success.toUpperCase()})`}
                                </p>
                                {isCriticalFail && (
                                  <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-red-700">
                                    <FaExclamationTriangle aria-hidden="true" /> Critical: a violation will fail the entire call
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-xs font-semibold text-gray-800 mb-1">
                                  Reason:
                                </label>
                                <textarea
                                  value={editableConduct[key]?.reason || ''}
                                  onChange={(e) => handleConductChange(key, 'reason', e.target.value)}
                                  className="input-field w-full !text-sm !py-1.5 !px-2.5 resize-none"
                                  rows="2"
                                  placeholder="Enter reason..."
                                  disabled={isAssessed}
                                />
                              </div>

                              {/* Correct a pre-check the gate got wrong. This records what the
                                  reviewer found - the answer above is what decides the verdict. */}
                              <PreCheckStepsEditor
                                parsed={parsed}
                                edited={editableSteps[key] || {}}
                                onChange={(step, field, value) => handleStepChange(key, step, field, value)}
                                disabled={isAssessed}
                              />
                              {stepAnswerConflict(editableSteps[key], editableConduct[key]?.answer) && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                  ⚠ {stepAnswerConflict(editableSteps[key], editableConduct[key]?.answer)}
                                </p>
                              )}

                              {parsed.evidence?.agent && (
                                <div className="text-gray-600">• Agent: "{parsed.evidence.agent}"</div>
                              )}
                              {parsed.evidence?.customer && (
                                <div className="text-gray-600">• Customer: "{parsed.evidence.customer}"</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {(qualityItems.length > 0 && !makeEdit) && (
                  <div>
                    <SectionHeading
                      title="Quality Assurance"
                      items={qualityItems.map(({ parsed }) => !isQualityItemFailed(parsed))}
                    />
                    <ul className="space-y-3">
                      {qualityItems.map(({ key, parsed }) => {
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;
                        const answer = answerOf(parsed);
                        const success = successOf(parsed);
                        const isCorrect = answer === success;
                        const failed = isQualityItemFailed(parsed);

                        /* Weighted yes/no questions and legacy scored questions carry the
                           score on different fields; both render through the same meter. */
                        const hasWeighting =
                          parsed.qaWeightAchieved !== undefined && parsed.qaWeighting !== undefined;
                        const score = hasWeighting ? parsed.qaWeightAchieved : parsed.score;
                        const max = hasWeighting ? parsed.qaWeighting : parsed.weight;

                        return (
                          <AssessmentCriterion
                            key={key}
                            name={key}
                            question={parsed.assessment}
                            tone={failed ? 'failed' : answer && !isCorrect ? 'neutral' : 'passed'}
                            statusLabel={failed ? 'Failed' : answer ? (isCorrect ? 'Met' : 'Not met') : undefined}
                            critical={isCriticalFail}
                            answer={answer}
                            expected={answer ? success : undefined}
                            score={score !== undefined && max !== undefined ? score : undefined}
                            max={score !== undefined && max !== undefined ? max : undefined}
                            reason={parsed.reason}
                            evidence={parsed.evidence || {}}
                          />
                        );
                      })}
                    </ul>
                  </div>
                )}

                {(qualityItems.length > 0 && makeEdit && !isAssessed) && (
                  <div>
                    <h5 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Quality (Edit Mode)</h5>
                    <ul className="space-y-4">
                      {qualityItems.map(({ key, parsed }) => {
                        const isYesNo = parsed.answer !== undefined && parsed.score === undefined;
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;

                        return (
                          <li key={key} className="text-sm border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="font-medium capitalize mb-2 flex items-center justify-between">
                              <span>{key}</span>
                              {isCriticalFail && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-lg font-semibold">
                                  CRITICAL
                                </span>
                              )}
                            </div>
                            {/* Display the assessment/question text */}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-2 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <div className="ml-3 space-y-2">
                              {/* Answer/Score Field */}
                              <div>
                                <label className="block text-xs font-semibold text-gray-800 mb-1">
                                  {isYesNo ? 'Answer:' : 'Score:'}
                                </label>
                                {isYesNo ? (
                                  /* Yes/No Dropdown */
                                  <>
                                    <SelectField
                                      value={editableQuality[key]?.answer || 'no'}
                                      onChange={(e) => handleQualityChange(key, 'answer', e.target.value)}
                                      className="select-field w-full !text-xs !py-1 !px-2"
                                      disabled={isAssessed}
                                    >
                                      <option value="yes">Yes</option>
                                      <option value="no">No</option>
                                    </SelectField>
                                    {isCriticalFail && (
                                      <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-red-700">
                                        <FaExclamationTriangle aria-hidden="true" /> Critical: "No" will fail the entire scorecard
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  /* Numeric Score Input */
                                  <>
                                    <input
                                      type="number"
                                      value={editableQuality[key]?.score || ''}
                                      onChange={(e) => handleQualityChange(key, 'score', e.target.value)}
                                      className="input-field w-full !text-xs !py-1 !px-2"
                                      disabled={isAssessed}
                                    />
                                    {parsed.weight !== undefined && (
                                      <span className="text-xs text-gray-600 ml-2">Out of {parsed.weight}</span>
                                    )}
                                  </>
                                )}
                              </div>

                              {/* Reason Input */}
                              <div>
                                <label className="block text-xs font-semibold text-gray-800 mb-1">
                                  Reason:
                                </label>
                                <textarea
                                  value={editableQuality[key]?.reason || ''}
                                  onChange={(e) => handleQualityChange(key, 'reason', e.target.value)}
                                  className="input-field w-full !text-sm !py-1.5 !px-2.5 resize-none"
                                  rows="2"
                                  placeholder="Enter reason..."
                                  disabled={isAssessed}
                                />
                              </div>

                              {/* Read-only evidence display */}
                              {parsed.evidence?.agent && (
                                <div className="text-gray-600">• Agent: "{parsed.evidence.agent}"</div>
                              )}
                              {parsed.evidence?.customer && (
                                <div className="text-gray-600">• Customer: "{parsed.evidence.customer}"</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    
                    {/* Preview of changes (optional, for debugging) */}
                    {/* <div className="mt-4 p-2 bg-cloudza-50 rounded-xl">
                      <h6 className="text-sm font-medium text-cloudza-800 mb-1">Changes Preview:</h6>
                      <pre className="text-sm text-cloudza-700">
                        {JSON.stringify(getQualityChanges(), null, 2)}
                      </pre>
                    </div> */}
                  </div>
                )}

                {makeEdit && !isAssessed && (
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <button
                      className='btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
                      onClick={() => handleSaveChanges()}
                      disabled={savingChanges}
                    >
                      {savingChanges ? (
                        <span className="flex items-center">
                          <InlineSpinner className="mr-2" />
                          Saving...
                        </span>
                      ) : (
                        <><FaSave /> Save Changes</>
                      )}
                    </button>
                    <button className='btn-secondary' onClick={() => setMakeEdit(false)} disabled={savingChanges}>
                      <FaTimes />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          } else {
            return (
              <div className="py-10 px-6 text-center">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                  <FaFileAlt className="text-lg" />
                </div>
                <h5 className="mt-3 text-sm font-semibold text-gray-800">No assessment details</h5>
                <p className="mt-1 text-sm text-gray-400">Run an assessment to see criteria and scores here.</p>
              </div>
            );
          }
        })()}
      </div>
    </div>

  </div>
);
  }

const TranscriptionText = ({ text }) => {
  const [showFull, setShowFull] = useState(false);
  if (!text) return null;
  const cutoff = 300;
  const shouldTruncate = text.length > cutoff;
  const display = shouldTruncate && !showFull ? text.substring(0, cutoff) + '...' : text;

  return (
    <div className="mt-2">
      <pre className="whitespace-pre-wrap text-sm text-gray-800">{display}</pre>
      {shouldTruncate && (
        <button
          className="text-sm text-cloudza-600 underline hover:text-cloudza-700 transition-colors"
          onClick={() => setShowFull((s) => !s)}
        >
          {showFull ? 'Show less' : 'See more'}
        </button>
      )}
    </div>
  );
};

// Mirrors TranscriptSegment's chat layout so the Q&A history reads like a
// continuation of the same conversation view: your questions sit on the
// right in a tinted bubble, the AI's answers on the left, white-bordered.
const GenAIBubble = ({ from, text, isSelf, pending }) => (
  <div className={`flex ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}>
    <div className={`min-w-0 max-w-[78%] flex flex-col ${isSelf ? 'items-end' : 'items-start'}`}>
      <span className={`text-xs font-semibold mb-1 ${isSelf ? 'text-cloudza-700' : 'text-gray-700'}`}>{from}</span>
      <div
        className={`px-3.5 py-2.5 text-sm leading-relaxed ${
          isSelf
            ? 'bg-cloudza-50 text-gray-800 rounded-2xl rounded-tr-sm'
            : 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-tl-sm'
        }`}
      >
        {pending ? (
          <span className="inline-flex items-center gap-1" aria-label="Thinking">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" />
          </span>
        ) : (
          <span className="whitespace-pre-line">{text}</span>
        )}
      </div>
    </div>
  </div>
);

const GenAITab = ({ summary, transcript, onRefr, history, onSubmit, setAlert }) => (
  <div className="space-y-6">
    {/* <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-gray-900">Summary</h3>
        <RefreshButton onRefresh={onRefr} setAlert={setAlert} className="text-sm" />
      </div>
      {summary.length > 0 ? (
        <dl className="space-y-2">
          {summary
            .filter(
              (e) =>
                e.label !== 'Validations' &&
                e.label !== 'Transcription' &&
                e.label !== 'Contact Summary' &&
                e.label !== 'Topic' &&
                e.label !== 'Product' &&
                e.label !== 'Resolved' &&
                e.label !== 'Actions'
            )
            .map((entry, i) => (
              <div key={i} className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">{entry.label}</dt>
                <dd className="text-sm text-gray-900 text-right whitespace-pre-line">{entry.value}</dd>
              </div>
            ))}
        </dl>
      ) : (
        <p className="text-sm text-gray-500">No Summary Available</p>
      )}
      <TranscriptionText text={transcript} />
    </div> */}
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-gray-900">Generative AI Query</h4>
      {history.length === 0 ? (
        <p className="text-sm text-gray-500">Ask a question about this call.</p>
      ) : (
        <div className="space-y-3">
          {history.map((entry, idx) => (
            <React.Fragment key={idx}>
              <GenAIBubble from="You" text={entry.question} isSelf />
              <GenAIBubble from="AI" text={entry.answer} pending={entry.answer === '...'} />
            </React.Fragment>
          ))}
        </div>
      )}
      <ChatInput onSubmit={onSubmit} />
    </div>
  </div>
);

export default Dashboard;
